import _ from 'lodash';
import React from 'react';
import { ReactComponent as MessageIcon } from 'src/assets/icons/email.svg';
import { ReactComponent as MailIcon } from 'src/assets/icons/mail.svg';
import { ReactComponent as LocationIcon } from 'src/assets/icons/navigationLine.svg';
import { ReactComponent as CallIcon } from 'src/assets/icons/phoneLine.svg';
import { Keys, locale } from 'src/i18n';
import { maskValue } from 'src/models/lmsConfig/queries';
import { generateRandomCode } from 'src/workspace/utils';
import { REVYRE } from '../constant';

export const getProfileInputs = (props, details, revyreConfig) => {
  if (!details) return [];
  if (props.revyre) {
    const { workflowType, domainType } = props;

    const moduleConfig =
      domainType !== 'workflow'
        ? revyreConfig?.[domainType]
        : revyreConfig?.[workflowType];
    const inputs = moduleConfig?.detailsConfig?.inputs ?? {};
    const { profileCardData, inputsMap } = details ?? {};

    const headerInputCodes = (profileCardData?.header || []).map(
      (input) => input?.profileField,
    );
    const bodyInputCodes = (profileCardData?.body || []).map(
      (input) => input?.profileField,
    );

    const headerInputs = (headerInputCodes || [])
      ?.map((code) => {
        const input = _.find(inputs, { code });

        if (input) {
          return {
            ...input,
            value: _.get(inputsMap, code),
          };
        }
        return {
          code,
          type: 'text',
          hint: code === 'currentState.stateId' ? 'Current State' : code,
          value: _.get(inputsMap, code),
        };
      })
      .filter((inp) => inp?.value && inp.hint);
    const bodyInputs = (bodyInputCodes || [])
      ?.map((code) => {
        const input = _.find(inputs, { code });

        if (input) {
          return {
            ...input,
            value: _.get(inputsMap, code),
          };
        }
        return {
          code,
          type: 'text',
          hint: code === 'currentState.stateId' ? 'Current State' : code,
          value: _.get(inputsMap, code),
        };
      })
      .filter((inp) => inp?.value && inp.hint);

    return [...headerInputs, ...bodyInputs];
  }
  return [];
};

export const getProfileName = (props, details) => {
  if (props.revyre) {
    return details?.inputsMap?.customerName || '';
  }
  return '';
};

export const getProfileCardFormValues = (props, inputs, revyreConfig) => {
  const valuesMap: Record<string, any> = {};

  if (!_.isEmpty(inputs)) {
    inputs.forEach((input) => {
      let value = input?.value;

      if (props.revyre && input.code === 'currentState.stateId') {
        const { workflowType, domainType } = props;
        const typeKey = workflowType || domainType;
        const states = revyreConfig?.[typeKey]?.states || [];
        const correspondingState = states.find((event) => event?.id === value);
        value = correspondingState?.name;
      }
      valuesMap[input?.code] = {
        value,
      };
    });
  }

  return valuesMap;
};

export const transformQuickAction = (
  action: { type: string; items: any[] },
  profiles: any,
) => {
  const fieldMap: Record<string, any> = {
    call: 'phoneNumbers',
    sms: 'phoneNumbers',
    email: 'emailIDs',
    navigate: 'locations',
  };

  const fieldToAdd = fieldMap[action.type];
  if (!fieldToAdd) return action;

  const transformedItems = profiles.flatMap((profile) => {
    const items = profile?.[fieldToAdd];
    if (!items || items.length === 0) {
      return [];
    }

    const processedItems = items.map((item) => {
      if (action.type === 'call') {
        return {
          ...item,
          profileId: profile?.profileId,
        };
      }

      if (!item?.masked) return item;

      if (action.type === 'navigate') {
        return {
          ...item,
          value: {
            ...item.value,
            address_string: maskValue(item?.value?.address_string, 1),
          },
        };
      }

      return {
        ...item,
        value: maskValue(item.value, 1),
      };
    });

    return [
      {
        type: profile.type,
        name: profile.name,
        description: profile.description,
        items: processedItems,
      },
    ];
  });

  return {
    ...action,
    items: transformedItems,
  };
};

export const actionConfigMap = {
  call: {
    icon: React.createElement(CallIcon),
    getSubLabel: (item) => item.value,
  },
  sms: {
    icon: React.createElement(MessageIcon),
    getSubLabel: (item) => item.value,
  },
  email: {
    icon: React.createElement(MailIcon),
    getSubLabel: (item) => item.value,
  },
  navigate: {
    icon: React.createElement(LocationIcon),
    getSubLabel: (item) => item.value.address_string || '',
  },
};

export const buildProfileActionOptions = (props, action) => {
  if (props.revyre) {
    const { type: actionType, items } = action;

    const config = actionConfigMap[actionType] || {
      icon: null,
      getSubLabel: (item) => item.value,
    };

    return items.map((profile) => {
      const options = (profile.items || []).map((item) => ({
        ...item,
        icon: config.icon,
        subLabel: config.getSubLabel(item),
      }));

      return {
        subLabel: profile.description,
        type: profile.type,
        name: profile.name,
        label: profile.name,
        value: `${profile.type}-${profile.name}-${generateRandomCode()}`,
        options,
      };
    });
  }
  return [];
};

export const getQuickActionButtonHint = (type) => {
  switch (type) {
    case 'email':
      return locale(Keys.EMAIL);
    case 'navigate':
      return locale(Keys.NAVIGATE);
    case 'print':
      return locale(Keys.PRINT);
    case 'call':
      return 'Call';
    case 'sms':
      return 'SMS';
    default:
      return '';
  }
};

export const handleQuickActionItemClick = (type: string, item) => {
  switch (type) {
    case 'navigate':
      if (item.url) {
        window.open(item.url, '_blank');
      }
      break;

    case 'email':
      if (item.value) {
        window.open(`mailto:${item.value}`, '_blank');
      }
      break;

    case 'call':
      if (item.value) {
        window.open(`tel:${item.value}`, '_blank');
      }
      break;

    case 'sms':
      if (item.value) {
        window.open(`sms:${item.value}`, '_blank');
      }
      break;

    default:
  }
};

export const getIsClickToCallEnabled = (props, details, revyreConfig) => {
  if (props.revyre) {
    return (
      revyreConfig[details?.inputsMap?.workflowType || props.workflowType]
        ?.featuresConfig?.atcConfig?.enableClickToCall || false
    );
  }
  return false;
};

export const getWorkflowCode = (inputValue) => {
  let parsedValue;

  try {
    parsedValue = JSON.parse(inputValue);
  } catch {
    parsedValue = inputValue;
  }

  const workflowCode = Array.isArray(parsedValue)
    ? parsedValue?.[0]?.code ?? ''
    : parsedValue;

  return workflowCode;
};

export const shouldShowQuickActions = ({ inputsMap, workflowType }: any) => {
  if (!inputsMap) return true;
  if (REVYRE.COLLECT_IQ.RESTRICT_HEADER_BUTTONS.includes(workflowType || '')) {
    return false;
  }

  const workflowDisableKey =
    REVYRE.COLLECT_IQ.DETAILS_PAGE_QUICK_ACTIONS_HIDDEN_RULES.hiddenByInputKey;

  return (
    !inputsMap?.[workflowDisableKey] &&
    !REVYRE.COLLECT_IQ.DETAILS_PAGE_QUICK_ACTIONS_HIDDEN_RULES.hiddenStatuses.includes(
      inputsMap?.stateId || inputsMap?.currentState?.stateId,
    )
  );
};
